
import moment from 'moment';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { eraseCookie, getCookie, setCookie } from '../../helpers/cookies-handler';
import { hideModal, showModal } from '../../store/actions/modal';
import { User } from '../../types';
import ModalAvatar from './components/ModalAvatar';
import ModalPhoneNumber from './components/ModalPhoneNumber';
import ModalSelectCountry from './components/ModalSelectCountry';

// Tipado de cada modal
interface ModalDefinition {
  name: string;
  shouldShow: boolean; // si el usuario necesita ver este modal (por lógica de datos)
  showOnPages: string[]; // en qué páginas se debe mostrar
  onlyFor: ('expert' | 'subscriber' | 'any')[]; // a quién se aplica
  onClose: () => void; // qué hacer al cerrarlo (normalmente nextModal)
  showAfterDays?: number; // cuántos días deben pasar para volver a mostrarlo
}

interface OnboardingModalsContextType {
  nextModal: () => void;
  currentModalName: string | null;
  hasPendingModals: boolean;
}

const OnboardingModalsContext = createContext<OnboardingModalsContextType>({
  nextModal: () => { },
  currentModalName: null,
  hasPendingModals: false,
});

export const useOnboardingModals = () => useContext(OnboardingModalsContext);

interface Props {
  children: React.ReactNode;
  user: User;
  userStates: any;
  isLoggedIn: boolean;
  currentPage: string;
  preferences: any;
  preferencesStates: any;
}

const OnboardingModalsProvider: React.FC<Props> = ({
  children,
  user,
  userStates,
  isLoggedIn,
  currentPage,
  preferences,
  preferencesStates,
}) => {
  const dispatch = useDispatch();

  // Cola de modales que se deben mostrar al usuario
  const [modalQueue, setModalQueue] = useState<ModalDefinition[]>([]);

  // Índice del modal actual
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  // Estado para controlar si hay un modal activo
  const [isModalActive, setIsModalActive] = useState<boolean>(false);

  // Avanza al siguiente modal
  const nextModal = () => {
    setIsModalActive(false);
    dispatch(hideModal());
    const nextIndex = currentIndex + 1;
    if (nextIndex < modalQueue.length) {
      setCurrentIndex(nextIndex);
      // Mostrar el siguiente modal después de un pequeño delay
      setTimeout(() => {
        setIsModalActive(true);
        dispatch(showModal(modalQueue[nextIndex].name));
      }, 500);
    }
  };

  // Genera el nombre único de la cookie por usuario y modal
  const getModalCookieKey = (modalName: string, userId: string | number) =>
    `onboarding_${modalName}_user_${userId}`;

  const role = useMemo(() => (
    !!user?.expert ? 'expert' : 'subscriber'
  ), [user])

  const isExpert = useMemo(() => (
    !!user?.expert
  ), [user])

  // Definicion de todos los modales y filtra cuáles deben mostrarse
  const modals: ModalDefinition[] = useMemo(() => {
    const allModals: ModalDefinition[] = [
      {
        name: 'modal-phone-number',
        shouldShow: !!user && !user?.phone_number && isLoggedIn,
        showOnPages: [
          "GROUP_DETAILS_SURVEY",
          "GROUP_DETAILS_LIBRARY",
          "GROUP_DETAILS_ACTIVITY_SUMMARY",
          "GROUP_DETAILS_SESSIONS",
          "GROUP_DETAILS_MODULES",
          "PREVIEW_COMMUNITY",
          "MY_PROFILE",
          "MANAGE_SUBSCRIPTIONS",
          "SUBSCRIPTIONS_LIST",

          "NEW_EXPERIENCE",
          "EDIT_EXPERIENCE",
          "LIST_EXPERIENCE",
          "PREVIEW_EXPERIENCE",
          "NEW_MEMBERSHIP",
          "EDIT_MEMBERSHIP",
          "LIST_MEMBERSHIP",
          "PREVIEW_MEMBERSHIP",
          "NEW_COMMUNITY_EVENT",
          "PREVIEW_COMMUNITY",
          "NEW_COMMUNITY",
          "EDIT_COMMUNITY",
          "LIST_COMMUNITY",
          "MEMBERS",
          "NEW_COUPON",
          "EDIT_COUPON",
          "LIST_COUPONS",
          "CONFIGURATION"
        ],
        onlyFor: ['any'],
        showAfterDays: 1,
        onClose: () => nextModal(),
      },
      {
        name: 'select-your-country',
        shouldShow: isExpert && !user?.expert?.billing_info?.country,
        showOnPages: [
          "MY_PROFILE",
          "NEW_EXPERIENCE",
          "EDIT_EXPERIENCE",
          "LIST_EXPERIENCE",
          "PREVIEW_EXPERIENCE",
          "NEW_MEMBERSHIP",
          "EDIT_MEMBERSHIP",
          "LIST_MEMBERSHIP",
          "PREVIEW_MEMBERSHIP",
          "NEW_COMMUNITY_EVENT",
          "PREVIEW_COMMUNITY",
          "NEW_COMMUNITY",
          "EDIT_COMMUNITY",
          "LIST_COMMUNITY",
          "MEMBERS",
          "NEW_COUPON",
          "EDIT_COUPON",
          "LIST_COUPONS",
          "CONFIGURATION",
        ],
        onlyFor: ['expert'],
        showAfterDays: 1,
        onClose: () => nextModal(),
      },
      {
        name: 'avatar-editor',
        shouldShow: !!user && !user?.profile_pic && isLoggedIn,
        showOnPages: [
          "GROUP_DETAILS_SURVEY",
          "GROUP_DETAILS_LIBRARY",
          "GROUP_DETAILS_ACTIVITY_SUMMARY",
          "GROUP_DETAILS_SESSIONS",
          "GROUP_DETAILS_MODULES",
          "PREVIEW_COMMUNITY",
          "MY_PROFILE",
          "MANAGE_SUBSCRIPTIONS",
          "SUBSCRIPTIONS_LIST",
          "NEW_EXPERIENCE",
          "EDIT_EXPERIENCE",
          "LIST_EXPERIENCE",
          "PREVIEW_EXPERIENCE",
          "NEW_MEMBERSHIP",
          "EDIT_MEMBERSHIP",
          "LIST_MEMBERSHIP",
          "PREVIEW_MEMBERSHIP",
          "NEW_COMMUNITY_EVENT",
          "NEW_COMMUNITY",
          "EDIT_COMMUNITY",
          "LIST_COMMUNITY",
          "MEMBERS",
          "NEW_COUPON",
          "EDIT_COUPON",
          "LIST_COUPONS",
          "CONFIGURATION"
        ],
        onlyFor: ['subscriber', 'expert'],
        showAfterDays: 7,
        onClose: () => nextModal(),
      },
    ]

    return allModals.filter((modal) => {
      const visibleByData =
        modal.shouldShow &&
        modal.showOnPages.includes(currentPage) &&
        (modal.onlyFor.includes('any') || modal.onlyFor.includes(role));

      let visibleByTime = true;

      if (user?.id && modal.showAfterDays !== undefined) {
        const cookieKey = getModalCookieKey(modal.name, user.id);
        const lastShown = getCookie(cookieKey)?.trim();

        if (lastShown) {
          const lastShownDate = moment(lastShown);
          const now = moment();
          const diffInDays = now.diff(lastShownDate, 'days');

          visibleByTime = diffInDays >= modal.showAfterDays;
        }
      }

      const shouldShowNow = visibleByData && visibleByTime;

      return shouldShowNow;
    });
  }, [user, preferences, currentPage, role, isExpert]);

  // Actualiza la cola de modales cuando cambia el resultado de useMemo
  useEffect(() => {
    setModalQueue(modals);
    setCurrentIndex(0);
  }, [modals]);

  // Muestra el primer modal y guarda la cookie si es necesario
  useEffect(() => {
    if (modalQueue.length > 0 && currentIndex < modalQueue.length && !isModalActive) {
      const modal = modalQueue[currentIndex];

      // Solo mostrar el modal si no hay otro modal activo
      setTimeout(() => {
        setIsModalActive(true);
        dispatch(showModal(modal.name));

        // Guardar cookie con fecha de visualización
        if (user?.id) {
          const cookieKey = getModalCookieKey(modal.name, user.id);
          if (!getCookie(cookieKey)) {
            // Usar showAfterDays del modal para la duración de la cookie
            const cookieDuration = modal.showAfterDays || 1; // fallback a 1 día si no está definido
            setCookie(cookieKey, new Date().toISOString(), cookieDuration);
          }
        }
      }, 1000); // Delay de 1 segundo para asegurar que no hay otros modales activos
    }
  }, [modalQueue, currentIndex, dispatch, user?.id, isModalActive]);

  const currentModalName =
    modalQueue.length > 0 && currentIndex < modalQueue.length
      ? modalQueue[currentIndex].name
      : null;

  return (
    <OnboardingModalsContext.Provider
      value={{
        nextModal,
        currentModalName,
        hasPendingModals: currentIndex < modalQueue.length - 1,
      }}
    >
      {children}
      <ModalAvatar />
      <ModalPhoneNumber />
      <ModalSelectCountry />
    </OnboardingModalsContext.Provider>
  );
};

const mapStateToProps = ({ userStore, pageStore, preferencesStore }) => {
  const { isLoggedIn } = userStore.isLoggedIn;
  const { data: user, states: userStates } = userStore.information;
  const currentPage = pageStore?.name;
  const { data: preferences, states: preferencesStates } =
    preferencesStore.preferences;

  return {
    user,
    userStates,
    isLoggedIn,
    currentPage,
    preferences,
    preferencesStates,
  };
};

export default connect(mapStateToProps)(OnboardingModalsProvider);


